import { useEffect, useState } from 'react';
import {
  collection,
  getCountFromServer,
  limit,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import {
  PackageSearch,
  Truck,
  UserCircle,
  Users,
  Bell,
} from 'lucide-react';
import { db } from '../lib/firebase';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/Form';

interface Stat {
  label: string;
  value: string | number;
  hint?: string;
  to: string;
  Icon: typeof PackageSearch;
}

// Non-terminal order statuses (OrderStatus enum in delivery_shared).
const ACTIVE_ORDER_STATUSES = [
  'pending',
  'confirmed',
  'driverAssigned',
  'enRoute',
  'pickedUp',
  'inTransit',
];

const REQUIRED_DOC_KEYS = [
  'driversLicense',
  'vehicleInsurance',
  'vehicleRegistration',
  'vehiclePhoto',
];

export default function DashboardPage() {
  const [stats, setStats] = useState<Stat[]>([
    { label: 'Active orders', value: '…', to: '/orders', Icon: PackageSearch },
    { label: 'Open jobs', value: '…', to: '/jobs', Icon: Truck },
    { label: 'Online drivers', value: '…', to: '/drivers', Icon: UserCircle },
    { label: 'Total customers', value: '…', to: '/customers', Icon: Users },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [actionCount, setActionCount] = useState(0);

  // Live count of admin action items (mirrors the Inbox page).
  useEffect(() => {
    const q = query(
      collection(db, 'users'),
      where('roles', 'array-contains', 'driver'),
      limit(500)
    );
    return onSnapshot(q, (snap) => {
      let count = 0;
      snap.forEach((d) => {
        const data = d.data() as {
          driverDocuments?: Record<
            string,
            { status?: string; expiresAt?: string | null }
          >;
          driverVerified?: boolean;
          verificationStatus?: string;
        };
        const docs = data.driverDocuments ?? {};
        const pending = REQUIRED_DOC_KEYS.some(
          (k) => docs[k]?.status === 'pending_review'
        );
        const expired = REQUIRED_DOC_KEYS.some((k) => {
          const e = docs[k];
          return (
            e?.status === 'approved' &&
            e.expiresAt != null &&
            new Date(e.expiresAt).getTime() < Date.now()
          );
        });
        const allApproved = REQUIRED_DOC_KEYS.every(
          (k) => docs[k]?.status === 'approved'
        );
        const readyToVerify =
          allApproved &&
          !expired &&
          !data.driverVerified &&
          data.verificationStatus !== 'rejected';
        if (pending) count++;
        if (expired) count++;
        if (readyToVerify) count++;
      });
      setActionCount(count);
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [activeOrders, openJobs, onlineDrivers, customers] =
          await Promise.all([
            getCountFromServer(
              query(
                collection(db, 'orders'),
                where('status', 'in', ACTIVE_ORDER_STATUSES)
              )
            ),
            getCountFromServer(
              query(
                collection(db, 'jobs'),
                where('status', '==', 'available')
              )
            ),
            getCountFromServer(
              query(collection(db, 'users'), where('isOnline', '==', true))
            ),
            getCountFromServer(
              query(
                collection(db, 'users'),
                where('roles', 'array-contains', 'customer')
              )
            ),
          ]);
        setStats([
          {
            label: 'Active orders',
            value: activeOrders.data().count,
            to: '/orders',
            Icon: PackageSearch,
          },
          {
            label: 'Open jobs',
            value: openJobs.data().count,
            to: '/jobs',
            Icon: Truck,
          },
          {
            label: 'Online drivers',
            value: onlineDrivers.data().count,
            to: '/drivers',
            Icon: UserCircle,
          },
          {
            label: 'Total customers',
            value: customers.data().count,
            to: '/customers',
            Icon: Users,
          },
        ]);
      } catch (e) {
        console.error('Failed to load dashboard stats', e);
        setError(
          `Failed to load dashboard stats: ${e instanceof Error ? e.message : e}`
        );
      }
    })();
  }, []);

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Snapshot of platform activity. All edits to runtime config take effect within 5 minutes."
      />
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {actionCount > 0 && (
        <Link
          to="/inbox"
          className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 transition hover:bg-amber-100"
        >
          <div className="flex items-center gap-3">
            <Bell size={18} className="text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              {actionCount} item{actionCount > 1 ? 's' : ''} need your attention
            </span>
          </div>
          <span className="text-xs font-semibold text-amber-700">
            Open Inbox →
          </span>
        </Link>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map(({ label, value, hint, to, Icon }) => (
          <Link key={label} to={to} className="group">
            <Card>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {label}
                  </div>
                  <div className="mt-2 text-3xl font-semibold text-slate-900">
                    {value}
                  </div>
                  {hint && (
                    <div className="mt-1 text-xs text-slate-500">{hint}</div>
                  )}
                </div>
                <div className="rounded-lg bg-brand-50 p-2 text-brand-600 transition group-hover:bg-brand-100">
                  <Icon size={20} />
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </>
  );
}
